import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import wordmarkWhite from "@/assets/techtail-wordmark-pixel.png";
import { submitLead } from "@/lib/leadSubmit";
import {
  divisionFromPath,
  divisionFromSpace,
  isAcceptableEmail,
  isDisposableEmail,
  isValidPhone,
  type Division,
} from "@/lib/leadGuard";
import { countryNameEs, isCountryCode } from "@/lib/countries";
import { CountrySelect, HoneypotField } from "./LeadFields";
import type { ContactRequestType } from "./ContactFormContext";
import { useLeadSuccess } from "@/hooks/useLeadSuccess";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-4 w-4" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

type Answers = {
  nombre: string;
  apellidos: string;
  empresa: string;
  cargo: string;
  email: string;
  telefono: string;
  pais: string;
  ciudad: string;
  espacio: string;
  ubicaciones: string;
  plazo: string;
};

const emptyAnswers: Answers = {
  nombre: "",
  apellidos: "",
  empresa: "",
  cargo: "",
  email: "",
  telefono: "",
  pais: "",
  ciudad: "",
  espacio: "",
  ubicaciones: "",
  plazo: "",
};

// Canonical values stored in the database / CRM; labels are translated for display.
export const ESPACIOS = [
  "gasolinera", "hotel", "gimnasio", "campus", "coworking", "camping", "estadio", "industria", "hospital", "otro",
] as const;
export const UBICACIONES = ["1", "2-5", "6-20", "20+"] as const;
export const UBICACIONES_KEYS = ["one", "twoFive", "sixTwenty", "moreTwenty"] as const;
export const PLAZOS = ["<3m", "3-6m", ">6m", "info"] as const;
export const PLAZOS_KEYS = ["lt3", "m3to6", "gt6", "info"] as const;

export const ESPACIO_ES: Record<string, string> = {
  gasolinera: "Gasolinera / estación de servicio", hotel: "Hotel", gimnasio: "Gimnasio",
  campus: "Campus o universidad", coworking: "Coworking", camping: "Camping", estadio: "Estadio",
  industria: "Industria o planta", hospital: "Hospital o laboratorio", otro: "Otro",
};
export const UBIC_ES: Record<string, string> = { "1": "1", "2-5": "2-5", "6-20": "6-20", "20+": "Más de 20" };
export const PLAZO_ES: Record<string, string> = {
  "<3m": "Menos de 3 meses", "3-6m": "3-6 meses", ">6m": "Más de 6 meses", info: "Solo me informo",
};

const STEPS = ["intro", "nombre", "empresa", "contacto", "ubicacion", "espacio", "ubicaciones", "plazo", "final"] as const;
type StepId = (typeof STEPS)[number];

const inputClass =
  "w-full border-b border-white/30 bg-transparent py-3 text-xl md:text-2xl text-white placeholder:text-white/35 focus:border-brand-blue focus:outline-none";
const selectClass = `${inputClass} appearance-none rounded-none`;

const Question = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="w-full max-w-3xl">
    <h2 className="font-serif text-2xl md:text-4xl leading-tight text-white">{title}</h2>
    {hint && <p className="mt-3 text-sm md:text-base text-white/50">{hint}</p>}
    <div className="mt-8 space-y-3">{children}</div>
  </div>
);

const OptionButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left border px-5 py-4 text-base md:text-lg transition-colors ${
      selected
        ? "border-brand-blue bg-brand-blue text-black"
        : "border-white/25 text-white hover:border-white hover:bg-white/5"
    }`}
  >
    {label}
  </button>
);

interface Props {
  open: boolean;
  onClose: () => void;
  division?: Division;
  requestType: ContactRequestType;
}

const ContactForm = ({ open, onClose, division: forcedDivision, requestType }: Props) => {
  const onSuccess = useLeadSuccess();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const schema = useMemo(
    () =>
      z.object({
        nombre: z.string().trim().min(1).max(100),
        apellidos: z.string().trim().min(1).max(100),
        empresa: z.string().trim().min(1).max(100),
        cargo: z.string().trim().max(100),
        email: z.string().trim().max(255).refine(isAcceptableEmail),
        telefono: z.string().trim().max(30).refine(isValidPhone),
        pais: z.string().trim().refine(isCountryCode),
        ciudad: z.string().trim().min(1).max(100),
        espacio: z.string().min(1),
        ubicaciones: z.string().min(1),
        plazo: z.string().min(1),
      }),
    [],
  );
  const [a, setA] = useState<Answers>(emptyAnswers);
  const [index, setIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(0);

  const step: StepId = STEPS[Math.min(index, STEPS.length - 1)];
  const stepNumber = STEPS.indexOf(step);
  const total = STEPS.length - 2;
  const isStudy = requestType === "estudio-implantacion";

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    setA(emptyAnswers);
    setIndex(0);
    setError(null);
    setHoneypot("");
    startedAt.current = Date.now();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const next = () => setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));
  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((p) => ({ ...p, [k]: v }));
  const pick = <K extends keyof Answers>(k: K, v: Answers[K]) => {
    set(k, v);
    setTimeout(next, 180);
  };

  const emailError =
    a.email.trim().length > 4 && !isAcceptableEmail(a.email)
      ? isDisposableEmail(a.email)
        ? t("sLeadFields.disposableEmail")
        : t("sLeadFields.invalidEmail")
      : null;
  const phoneError = a.telefono.trim().length > 5 && !isValidPhone(a.telefono) ? t("sLeadFields.invalidPhone") : null;

  const canContinue = () => {
    switch (step) {
      case "nombre":
        return a.nombre.trim().length > 0 && a.apellidos.trim().length > 0;
      case "empresa":
        return a.empresa.trim().length > 0;
      case "contacto":
        return isAcceptableEmail(a.email) && isValidPhone(a.telefono);
      case "ubicacion":
        return a.ciudad.trim().length > 0 && a.pais !== "";
      case "espacio":
        return a.espacio !== "";
      case "ubicaciones":
        return a.ubicaciones !== "";
      case "plazo":
        return a.plazo !== "";
      default:
        return true;
    }
  };

  const submit = async (answers: Answers) => {
    const parsed = schema.safeParse(answers);
    if (!parsed.success) {
      setError(t("sFormsContact.errors.checkData"));
      return;
    }
    setSending(true);
    setError(null);

    const d = parsed.data;
    const division = forcedDivision ?? divisionFromSpace(d.espacio, divisionFromPath(pathname));
    const paisEs = countryNameEs(d.pais);
    const core = {
      nombre: `${d.nombre} ${d.apellidos}`,
      empresa: d.empresa,
      email: d.email,
      telefono: d.telefono,
      pais: paisEs,
      mensaje: [
        isStudy ? "ESTUDIO DE IMPLANTACIÓN" : "CONTACTO",
        `Espacio: ${ESPACIO_ES[d.espacio] ?? d.espacio}`,
        `Ubicaciones: ${UBIC_ES[d.ubicaciones] ?? d.ubicaciones}`,
        `Plazo: ${PLAZO_ES[d.plazo] ?? d.plazo}`,
        `Cargo: ${d.cargo || "—"}`,
        `Ciudad: ${d.ciudad}`,
      ].join("\n"),
    };
    const qualification = {
      tipo_espacio: d.espacio,
      num_ubicaciones: d.ubicaciones,
      plazo: d.plazo,
      request_type: requestType,
    };

    const res = await submitLead({
      core,
      extras: {
        ...qualification,
        division,
        form_name: "contacto",
        cargo: d.cargo || null,
        ciudad: d.ciudad,
        country_code: d.pais,
      },
      lang: i18n.language,
      honeypot,
      startedAt: startedAt.current,
    });
    setSending(false);
    if (!res.ok) {
      setError(t("sFormsContact.errors.sendFailed"));
      return;
    }
    onClose();
    onSuccess("contacto", undefined, { ...qualification, division, spam: res.spam });
  };

  const pickPlazo = (v: string) => {
    const nextAnswers = { ...a, plazo: v };
    setA(nextAnswers);
    void submit(nextAnswers);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <HoneypotField value={honeypot} onChange={setHoneypot} />
          <div className="flex items-center justify-between px-5 md:px-10 py-5">
            <div className="flex items-center gap-4">
              <img src={wordmarkWhite} alt="TECHTAIL" className="h-5 md:h-6 w-auto" />
              {step !== "intro" && step !== "final" && (
                <span className="font-dot text-[11px] md:text-xs uppercase tracking-widest text-brand-blue">
                  {`// ${String(stepNumber).padStart(2, "0")} / ${total}`}
                </span>
              )}
            </div>
            {step !== "intro" && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 text-sm hover:text-white transition-colors"
              >
                {t('sFormsContact.close')}
              </button>
            )}
          </div>

          <div className="h-px w-full bg-white/10">
            <div
              className="h-px bg-brand-blue transition-all duration-300"
              style={{ width: `${(stepNumber / (STEPS.length - 1)) * 100}%` }}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="container mx-auto px-5 md:px-10 py-8 md:py-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  {step === "intro" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {isStudy ? t('sFormsContact.introStudy.title') : t('sFormsContact.intro.title')}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60 max-w-xl">
                        {isStudy ? t('sFormsContact.introStudy.description') : t('sFormsContact.intro.description')}
                      </p>
                      <button
                        type="button"
                        onClick={next}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                        >
                        {t('sFormsContact.intro.start')}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}

                  {step === "nombre" && (
                    <Question title={t('sFormsContact.steps.nombre.title')}>
                      <input
                        autoFocus
                        value={a.nombre}
                        maxLength={100}
                        onChange={(e) => set("nombre", e.target.value)}
                        placeholder={t('sFormsContact.steps.nombre.nombrePlaceholder')}
                        className={inputClass}
                      />
                      <input
                        value={a.apellidos}
                        maxLength={100}
                        onChange={(e) => set("apellidos", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t('sFormsContact.steps.nombre.apellidosPlaceholder')}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "empresa" && (
                    <Question title={t("sFormsContact.steps.empresa.title")}>
                      <input
                        autoFocus
                        value={a.empresa}
                        maxLength={100}
                        onChange={(e) => set("empresa", e.target.value)}
                        placeholder={t("sFormsContact.steps.empresa.empresaPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.cargo}
                        maxLength={100}
                        onChange={(e) => set("cargo", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsContact.steps.empresa.cargoPlaceholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "contacto" && (
                    <Question title={t("sFormsContact.steps.email.title")} hint={t("sFormsContact.steps.telefono.hint")}>
                      <input
                        autoFocus
                        type="email"
                        value={a.email}
                        maxLength={255}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder={t("sFormsContact.steps.email.placeholder")}
                        className={inputClass}
                      />
                      {emailError && <p className="text-sm text-red-400">{emailError}</p>}
                      <input
                        type="tel"
                        value={a.telefono}
                        maxLength={30}
                        onChange={(e) => set("telefono", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsContact.steps.telefono.placeholder")}
                        className={inputClass}
                      />
                      {phoneError && <p className="text-sm text-red-400">{phoneError}</p>}
                    </Question>
                  )}

                  {step === "ubicacion" && (
                    <Question title={t("sFormsContact.steps.ubicacion.title")}>
                      <CountrySelect autoFocus value={a.pais} onChange={(v) => set("pais", v)} className={selectClass} />
                      <input
                        value={a.ciudad}
                        maxLength={100}
                        onChange={(e) => set("ciudad", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsContact.steps.ubicacion.ciudadPlaceholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "espacio" && (
                    <Question title={t("sFormsContact.steps.espacio.title")}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {ESPACIOS.map((o) => (
                          <OptionButton
                            key={o}
                            label={t(`sFormsContact.steps.espacio.options.${o}`)}
                            selected={a.espacio === o}
                            onClick={() => pick("espacio", o)}
                          />
                        ))}
                      </div>
                    </Question>
                  )}

                  {step === "ubicaciones" && (
                    <Question title={t("sFormsContact.steps.ubicaciones.title")}>
                      {UBICACIONES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={t(`sFormsContact.steps.ubicaciones.options.${UBICACIONES_KEYS[i]}`)}
                          selected={a.ubicaciones === o}
                          onClick={() => pick("ubicaciones", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "plazo" && (
                    <Question title={t("sFormsContact.steps.plazo.title")}>
                      {PLAZOS.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={t(`sFormsContact.steps.plazo.options.${PLAZOS_KEYS[i]}`)}
                          selected={a.plazo === o}
                          onClick={() => !sending && pickPlazo(o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "final" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t('sFormsContact.final.title', { name: a.nombre ? `, ${a.nombre.trim()}` : "" })}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60 max-w-xl">
                        {t('sFormsContact.final.description', { email: a.email })}
                      </p>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                      >
                        {t('sFormsContact.close')}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}

                  {error && <p className="mt-6 text-sm text-red-400">{error}</p>}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {step !== "intro" && step !== "final" && (
            <div className="border-t border-white/10 px-5 md:px-10 py-4 flex items-center justify-between">
              <button
                type="button"
                onClick={back}
                className="text-white/50 text-sm hover:text-white transition-colors"
              >
                {t('sFormsContact.nav.back')}
              </button>
              <button
                type="button"
                disabled={!canContinue() || sending}
                onClick={() => (step === "plazo" ? submit(a) : next())}
                className="inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base disabled:opacity-40"
              >
                {step === "plazo" ? (sending ? t('sFormsContact.nav.sending') : t('sFormsContact.nav.contact')) : t('sFormsContact.nav.continue')}
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                  <PixelArrow />
                </span>
              </button>
            </div>
          )}
          {step === "intro" && (
            <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4">
              <button
                type="button"
                aria-label={t("sFormsContact.close")}
                onClick={onClose}
                className="h-12 w-12 flex items-center justify-center border border-white/30 text-white hover:text-brand-blue transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactForm;
